import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import { EditOutlined } from '@ant-design/icons';
import { RootState } from '@/reducers/reducers';
import { AppComponentProps } from '@/typescriptHelpers';
import { SubscriptionFeatureType, TriageTicketFilters } from '@/types';
import { selectAppSettings } from '@/selectors/selectors';
import { useUpdateTriageTicketFiltersMutation } from '@/middlewares/defaultApi';
import { globalMessage } from '@/utils/globalMessage';
import { updateAppSettingsSuccessAction } from '@/actions/appSettings';
import { useRestrictedFeatureIsDisabled } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import NewTicketFilterModal, { TRIAGE_FILTER_FIELDS, TriageFilterField } from './NewTicketFilterModal';

const EMPTY_FILTERS: TriageTicketFilters = { issueTypes: null, labels: null, titleKeywords: null };

// Renders the content of the "Ticket filters" row; the parent owns the Descriptions.Item.
const TicketFiltersSettings: React.FC<AppComponentProps> = ({ orgId, appId }) => {
    const savedFilters = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.triageTicketFilters ?? EMPTY_FILTERS,
    );
    const dispatch = useDispatch();
    const isPaywalled = useRestrictedFeatureIsDisabled(SubscriptionFeatureType.Triage);
    const [updateTriageTicketFilters, { isLoading, data: updateResponse }] = useUpdateTriageTicketFiltersMutation();
    const [modalOpen, setModalOpen] = React.useState(false);

    React.useEffect(() => {
        if (updateResponse?.success) {
            dispatch(updateAppSettingsSuccessAction({ appId, appSettings: updateResponse.data }));
            globalMessage.success({ content: 'Ticket filters updated!', key: 'triageTicketFilters' });
            setModalOpen(false);
        } else if (updateResponse?.errorMessage) {
            globalMessage.error(updateResponse.errorMessage);
        }
    }, [updateResponse, appId, dispatch]);

    const saveField = (field: TriageFilterField, values: string[]) =>
        updateTriageTicketFilters({
            orgId,
            appId,
            filters: { ...savedFilters, [field]: values.length > 0 ? values : null },
        });

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, marginRight: 48, paddingTop: 16 }}>
                {TRIAGE_FILTER_FIELDS.map(({ value: field, label }, idx) => {
                    const values = savedFilters[field] ?? [];
                    return (
                        <React.Fragment key={field}>
                            <Text style={{ display: 'block', paddingBottom: 10 }}>{label}</Text>
                            {values.length > 0 ?
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {values.map((value) => (
                                        <li key={value}>{value}</li>
                                    ))}
                                </ul>
                            :   <Text type="secondary" style={{ display: 'block', paddingLeft: 8 }}>
                                    Not set
                                </Text>
                            }
                            {idx !== TRIAGE_FILTER_FIELDS.length - 1 && <Divider />}
                        </React.Fragment>
                    );
                })}
            </div>
            <PermissionedActionButton
                actionType={RunwayUserAction.UAAddRemoveEditFeatureAffiliations}
                type="link"
                icon={<EditOutlined />}
                disabled={isPaywalled}
                onClick={() => setModalOpen(true)}
            >
                Edit
            </PermissionedActionButton>

            <NewTicketFilterModal
                open={modalOpen}
                isLoading={isLoading}
                filters={savedFilters}
                onCancel={() => setModalOpen(false)}
                onOk={saveField}
            />
        </div>
    );
};

export default TicketFiltersSettings;
